import React, { useEffect, useState } from 'react';

import useOrgSettingsStore from '../../stores/orgSettingsStore';
import { usePermissions } from '../../context/PermissionContext';

/**
 * AdmissionModuleSettings — the per-org "Enable Admission (IPD) module" toggle.
 *
 * Lives on the admin Settings page (not behind the admission gate) so an
 * admin can switch it on. Reads/writes the single source of truth in
 * orgSettingsStore (enable_admission_module). When off, the whole module is
 * hidden. Unlike the lab card there is no "Open …" deep-link yet — no IPD
 * screen exists until B2.
 */
export default function AdmissionModuleSettings() {
  const enableAdmissionModule = useOrgSettingsStore((s) => s.enableAdmissionModule);
  const depositLowThresholdPaise = useOrgSettingsStore((s) => s.depositLowThresholdPaise);
  const admissionLoaded = useOrgSettingsStore((s) => s.admissionLoaded);
  const fetchAdmissionSettings = useOrgSettingsStore((s) => s.fetchAdmissionSettings);
  const setAdmissionModule = useOrgSettingsStore((s) => s.setAdmissionModule);
  const setDepositLowThreshold = useOrgSettingsStore((s) => s.setDepositLowThreshold);
  const { refreshPermissions } = usePermissions();

  const bedDayAccrualEnabled = useOrgSettingsStore((s) => s.bedDayAccrualEnabled);
  const setBedDayAccrual = useOrgSettingsStore((s) => s.setBedDayAccrual);

  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  // Threshold editor: rupees as typed; paise on the wire.
  const [thresholdRupees, setThresholdRupees] = useState('');
  const [thresholdBusy, setThresholdBusy] = useState(false);
  const [thresholdSaved, setThresholdSaved] = useState(false);
  const [thresholdErr, setThresholdErr] = useState(null);
  const [bedAccrualBusy, setBedAccrualBusy] = useState(false);
  const [bedAccrualErr, setBedAccrualErr] = useState(null);

  useEffect(() => {
    if (!admissionLoaded) fetchAdmissionSettings();
  }, [admissionLoaded, fetchAdmissionSettings]);

  // Seed the input from the cached value once it loads (rupees display).
  useEffect(() => {
    if (admissionLoaded) setThresholdRupees(String((Number(depositLowThresholdPaise) || 0) / 100));
  }, [admissionLoaded, depositLowThresholdPaise]);

  const thresholdNum = Number(thresholdRupees);
  const thresholdValid = Number.isFinite(thresholdNum) && thresholdNum >= 0;
  const thresholdDirty = thresholdValid
    && Math.round(thresholdNum * 100) !== (Number(depositLowThresholdPaise) || 0);

  const saveThreshold = async () => {
    if (!thresholdValid || thresholdBusy || !thresholdDirty) return;
    setThresholdBusy(true);
    setThresholdErr(null);
    setThresholdSaved(false);
    try {
      await setDepositLowThreshold(Math.round(thresholdNum * 100));
      setThresholdSaved(true);
    } catch (e) {
      setThresholdErr(e?.response?.data?.detail?.message || e?.message || 'Failed to save');
    } finally {
      setThresholdBusy(false);
    }
  };

  const onToggleBedAccrual = async () => {
    if (bedAccrualBusy) return;
    setBedAccrualBusy(true);
    setBedAccrualErr(null);
    try {
      await setBedDayAccrual(!bedDayAccrualEnabled);
    } catch (e) {
      setBedAccrualErr(e?.response?.data?.detail?.message || e?.response?.data?.detail || e?.message || 'Failed to update');
    } finally {
      setBedAccrualBusy(false);
    }
  };

  const onToggle = async () => {
    if (busy) return;
    setBusy(true);
    setErr(null);
    try {
      await setAdmissionModule(!enableAdmissionModule);
      // The backend strips/restores the admission permission slugs based on
      // this flag, so re-fetch the session to pick up (or drop) them WITHOUT
      // a re-login — otherwise the admin hits a wrongful "no permission" on
      // the IPD screens right after enabling.
      await refreshPermissions();
    } catch (e) {
      setErr(e?.response?.data?.detail || e?.message || 'Failed to update');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="rounded-2xl border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 p-5 mt-5 shadow-sm">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h3 className="text-base font-bold text-slate-800 dark:text-slate-100">🛏️ Admission (IPD) module</h3>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-1 max-w-xl">
            In-patient admissions for your organisation — beds, admissions, IPD billing.
            When off (the default), the app behaves exactly as today.
          </p>
        </div>
        <div className="flex flex-col items-end gap-1.5 shrink-0">
          <button
            type="button"
            role="switch"
            aria-checked={enableAdmissionModule}
            aria-label="Admission (IPD) module"
            onClick={onToggle}
            disabled={busy}
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors disabled:opacity-60 ${
              enableAdmissionModule ? 'bg-teal-600' : 'bg-slate-300 dark:bg-slate-600'
            }`}
          >
            <span
              className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${
                enableAdmissionModule ? 'translate-x-5' : 'translate-x-0.5'
              }`}
            />
          </button>
          <span
            className={`text-xs font-semibold ${
              enableAdmissionModule ? 'text-teal-700 dark:text-teal-300' : 'text-slate-500 dark:text-slate-400'
            }`}
          >
            {busy ? 'Saving…' : enableAdmissionModule ? 'Enabled' : 'Disabled'}
          </span>
        </div>
      </div>

      {err && <div className="text-xs text-rose-600 dark:text-rose-400 mt-2">{err}</div>}

      {enableAdmissionModule && (
        <div className="mt-5 pt-4 border-t border-slate-200 dark:border-slate-700" data-testid="deposit-threshold-setting">
          <h4 className="text-sm font-bold text-slate-800 dark:text-slate-100">Low-deposit alert</h4>
          <p className="text-xs text-slate-500 dark:text-slate-400 mt-1 max-w-xl">
            Flag an admission when the bill owed runs ahead of the deposit held by more than this amount.
            Set to <b>₹0</b> to alert as soon as the bill exceeds deposits. The desk shows a
            &ldquo;Deposit low&rdquo; flag and can filter for these admissions.
          </p>
          <div className="flex items-end gap-2 mt-3 flex-wrap">
            <div>
              <label htmlFor="deposit-threshold" className="block text-xs font-medium text-slate-500 dark:text-slate-400 mb-1">
                Alert headroom (₹)
              </label>
              <input
                id="deposit-threshold"
                type="number"
                min="0"
                step="1"
                value={thresholdRupees}
                onChange={(e) => { setThresholdRupees(e.target.value); setThresholdSaved(false); }}
                className="w-40 px-2.5 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 text-slate-800 dark:text-slate-100 text-sm"
                data-testid="deposit-threshold-input"
              />
            </div>
            <button
              type="button"
              onClick={saveThreshold}
              disabled={!thresholdValid || thresholdBusy || !thresholdDirty}
              className="px-4 py-2 rounded-lg bg-teal-600 text-white text-sm font-semibold disabled:opacity-60"
              data-testid="deposit-threshold-save"
            >
              {thresholdBusy ? 'Saving…' : 'Save'}
            </button>
            {thresholdSaved && !thresholdDirty && (
              <span className="text-xs font-semibold text-teal-700 dark:text-teal-300 mb-2">Saved</span>
            )}
          </div>
          {!thresholdValid && <div className="text-xs text-rose-600 dark:text-rose-400 mt-2">Enter ₹0 or more.</div>}
          {thresholdErr && <div className="text-xs text-rose-600 dark:text-rose-400 mt-2">{thresholdErr}</div>}
        </div>
      )}

      {enableAdmissionModule && (
        <div className="mt-5 pt-4 border-t border-slate-200 dark:border-slate-700" data-testid="bed-day-accrual-setting">
          <div className="flex items-start justify-between gap-4">
            <div>
              {/* COPY: pending Business ratification */}
              <h4 className="text-sm font-bold text-slate-800 dark:text-slate-100">Automatic bed-day charging</h4>
              <p className="text-xs text-slate-500 dark:text-slate-400 mt-1 max-w-xl">
                {/* COPY: pending Business ratification */}
                Turn on to charge bed-days automatically every day. Turn off (the default) to add bed charges
                manually on the admission&rsquo;s bill.
              </p>
            </div>
            <div className="flex flex-col items-end gap-1.5 shrink-0">
              <button
                type="button"
                role="switch"
                aria-checked={bedDayAccrualEnabled}
                aria-label="Automatic bed-day charging"
                onClick={onToggleBedAccrual}
                disabled={bedAccrualBusy}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors disabled:opacity-60 ${
                  bedDayAccrualEnabled ? 'bg-teal-600' : 'bg-slate-300 dark:bg-slate-600'
                }`}
                data-testid="bed-day-accrual-toggle"
              >
                <span
                  className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${
                    bedDayAccrualEnabled ? 'translate-x-5' : 'translate-x-0.5'
                  }`}
                />
              </button>
              <span
                className={`text-xs font-semibold ${
                  bedDayAccrualEnabled ? 'text-teal-700 dark:text-teal-300' : 'text-slate-500 dark:text-slate-400'
                }`}
              >
                {bedAccrualBusy ? 'Saving…' : bedDayAccrualEnabled ? 'Enabled' : 'Disabled'}
              </span>
            </div>
          </div>
          {bedAccrualErr && <div className="text-xs text-rose-600 dark:text-rose-400 mt-2">{bedAccrualErr}</div>}
        </div>
      )}
    </div>
  );
}
